import type Coupon from '@alltrails/types/Coupon';
import type Plan from '@alltrails/core/types/Plan';
import type { ApplePayRecurringPaymentDateUnit } from '@recurly/recurly-js';
import { FreeTrialCoupon } from '@alltrails/core/types/Coupon';
import type PurchaseTier from '@alltrails/core/types/PurchaseTier';
import { useIntl } from 'react-intl';
import type SubscriptionsNewResponse from '@alltrails/core/types/SubscriptionsNewResponse';
import { getPriceTotal } from '../utils/plan';

type Params = {
  canUpgradeExistingSubscription: boolean;
  coupon?: Coupon;
  isMultiyearConversion?: boolean;
  plan: Plan;
  purchaseTier: PurchaseTier;
  subscription: SubscriptionsNewResponse['subscription'];
  whenExpired?: string;
};

// [x] if the user does a multiyear conversion, it's their when_expired (returned by subscriptions/new)
// [x] if the user is doing a normal free trial, it's 7 days from now (and we want "now" to be as fresh as possible)
// [x] if the user is doing a normal sub, it's the current time
// [x] unless they are using a free trial coupon, in which case the coupon trial duration needs to be used
// [x] if the user is doing Plus-to-Peak and they're in a trial, it's the current period end date. But the client doesn't know they're in a trial at the moment.

const getIntervalUnit = (unit: FreeTrialCoupon['freeTrialUnit']): ApplePayRecurringPaymentDateUnit => {
  switch (unit) {
    case 'day':
      return 'day';
    case 'week':
      return 'day'; // Apple Pay doesn't support 'week', use 'day' instead
    case 'month':
      return 'month';
  }
};

function getFreeTrialCouponDate(coupon: FreeTrialCoupon) {
  const date = new Date();

  switch (coupon.freeTrialUnit) {
    case 'day':
      date.setDate(date.getDate() + coupon.freeTrialAmount);
    case 'week':
      date.setDate(date.getDate() + coupon.freeTrialAmount * 7);
    case 'month':
      date.setMonth(date.getMonth() + coupon.freeTrialAmount);
  }

  return date;
}

export default function useApplePayConfig({
  canUpgradeExistingSubscription,
  coupon,
  isMultiyearConversion,
  plan,
  purchaseTier,
  subscription,
  whenExpired
}: Params) {
  const intl = useIntl();

  const regularBillingLabel =
    purchaseTier === 'peak'
      ? intl.formatMessage({ defaultMessage: 'Peak subscription' })
      : intl.formatMessage({ defaultMessage: 'Plus subscription' });

  const trialBillingLabel = intl.formatMessage({ defaultMessage: 'Trial' });

  if (isMultiyearConversion && whenExpired) {
    return {
      paymentRequest: {
        recurringPaymentRequest: {
          paymentDescription: '', // does not appear to be used in the payment sheet apple pay documentation might be wrong?
          regularBilling: {
            label: regularBillingLabel,
            amount: getPriceTotal(plan, coupon).toString(),
            paymentTiming: 'recurring' as const,
            recurringPaymentIntervalUnit: 'year' as const,
            recurringPaymentStartDate: new Date(whenExpired)
          }
        },
        total: { label: 'AllTrails', amount: '0' }
      }
    };
  }

  if (coupon?.type === 'FreeTrialCoupon') {
    const date = getFreeTrialCouponDate(coupon);

    return {
      paymentRequest: {
        recurringPaymentRequest: {
          paymentDescription: '', // does not appear to be used in the payment sheet apple pay documentation might be wrong?
          regularBilling: {
            label: regularBillingLabel,
            amount: getPriceTotal(plan, coupon).toString(),
            paymentTiming: 'recurring' as const,
            recurringPaymentIntervalUnit: 'year' as const,
            recurringPaymentStartDate: date
          },
          trialBilling: {
            amount: '0',
            label: trialBillingLabel,
            paymentTiming: 'recurring' as const,
            recurringPaymentEndDate: date,
            recurringPaymentIntervalUnit: getIntervalUnit(coupon.freeTrialUnit)
          }
        },
        total: { label: 'AllTrails', amount: '0' }
      }
    };
  }

  // plus to peak
  if (canUpgradeExistingSubscription && subscription?.trial) {
    const endOfTrial = new Date(subscription.currentPeriodEndDate);

    return {
      paymentRequest: {
        recurringPaymentRequest: {
          paymentDescription: '', // does not appear to be used in the payment sheet apple pay documentation might be wrong?
          regularBilling: {
            amount: getPriceTotal(plan, coupon).toString(),
            label: regularBillingLabel,
            paymentTiming: 'recurring' as const,
            recurringPaymentIntervalUnit: 'year' as const,
            recurringPaymentStartDate: endOfTrial
          },
          trialBilling: {
            amount: '0',
            label: trialBillingLabel,
            paymentTiming: 'recurring' as const,
            recurringPaymentEndDate: endOfTrial,
            recurringPaymentIntervalUnit: 'day' as const
          }
        },
        total: { label: 'AllTrails', amount: '0' }
      }
    };
  }

  if (plan.isTrial) {
    const endOfTrial = new Date();
    endOfTrial.setDate(endOfTrial.getDate() + 7);

    return {
      paymentRequest: {
        recurringPaymentRequest: {
          paymentDescription: '', // does not appear to be used in the payment sheet apple pay documentation might be wrong?
          regularBilling: {
            amount: getPriceTotal(plan, coupon).toString(),
            label: regularBillingLabel,
            paymentTiming: 'recurring' as const,
            recurringPaymentIntervalUnit: 'year' as const,
            recurringPaymentStartDate: endOfTrial
          },
          trialBilling: {
            amount: '0',
            label: trialBillingLabel,
            paymentTiming: 'recurring' as const,
            recurringPaymentEndDate: endOfTrial,
            recurringPaymentIntervalUnit: 'day' as const
          }
        },
        total: { label: 'AllTrails', amount: '0' }
      }
    };
  }

  return {
    paymentRequest: {
      recurringPaymentRequest: {
        paymentDescription: '', // does not appear to be used in the payment sheet apple pay documentation might be wrong?
        regularBilling: {
          amount: getPriceTotal(plan, coupon).toString(),
          label: regularBillingLabel,
          paymentTiming: 'recurring' as const,
          recurringPaymentIntervalUnit: 'year' as const,
          recurringPaymentStartDate: new Date()
        }
      },
      total: { label: 'AllTrails', amount: getPriceTotal(plan, coupon).toString() }
    }
  };
}
